"use client";

import { useState } from "react";
import Image from "next/image";
import { ImageAssetResponsiveProps } from "./Image.types";
import assetLoader from "@utils/internalImageLoder";
import {
  useScreenSizeDetection,
  useImageSizeSelection,
  getImageStyle,
  useResponsiveProps,
} from "../ImageResponsive/BaseImageResponsive";

const ImageResponsiveAssets = ({
    filePath,
    name,
    alt = "image",
    width,
    height,
    className = "",
    fill,
    priority = false,
    style,
    onClick,
}: ImageAssetResponsiveProps) => {
    const [useOriginal, setUseOriginal] = useState(false);
    // Use common screen size detection
    const screenSize = useScreenSizeDetection();

    if (fill && (width || height)) {
        console.warn("Width and height props are ignored when fill is true.");
    }

    const baseUrl = `${assetLoader()}${filePath}`;
    const originalSrc = `${baseUrl}/original/${name}.webp`;

    const handleError = () => {
        setUseOriginal(true);
    };

    // Use common image size selection
    const effectiveWidth = screenSize.viewportWidth > 0 
      ? screenSize.viewportWidth 
      : screenSize.width > 0 
        ? screenSize.width 
        : width;

    const { imageSrc } = useImageSizeSelection(
      screenSize,
      effectiveWidth,
      baseUrl,
      name
    );

    // Use common responsive props
    const responsiveProps = useResponsiveProps(fill, width, height);

    // Use common image style
    const imageStyle = getImageStyle(fill, width, height, style);

    const imageProps = {
        ...responsiveProps,
        ...(priority ? { fetchPriority: "high" as const } : {})
    };
    
    return (
        <picture>
            {!useOriginal && (
                <source
                    type="image/webp"
                    srcSet={`
                        ${baseUrl}/small/${name}.webp 320w,
                        ${baseUrl}/small/${name}.webp 640w,
                        ${baseUrl}/medium/${name}.webp 1024w,
                        ${baseUrl}/large/${name}.webp 1280w,
                        ${baseUrl}/xlarge/${name}.webp 1920w,
                        ${baseUrl}/original/${name}.webp 2560w
                    `}
                    sizes={fill
                        ? '(min-width: 1920px) 1920px, (min-width: 1280px) 1280px, (min-width: 1024px) 1024px, (min-width: 640px) 640px, 320px'
                        : width
                            ? `(min-width: 1920px) ${Math.min(width, 1920)}px, (min-width: 1280px) ${Math.min(width, 1280)}px, (min-width: 1024px) ${Math.min(width, 1024)}px, (min-width: 640px) ${Math.min(width, 640)}px, ${width}px`
                            : '(min-width: 1920px) 1920px, (min-width: 1280px) 1280px, (min-width: 1024px) 1024px, (min-width: 640px) 640px, 320px'
                    }
                />
            )}

            <Image
                src={useOriginal ? originalSrc : imageSrc}
                alt={alt}
                {...imageProps}
                className={className}
                style={imageStyle}
                loading={priority ? "eager" : "lazy"}
                onError={handleError}
                onClick={onClick}
            />
        </picture>
    );
}

export default ImageResponsiveAssets;